import React, { useRef, useState, ReactElement, ReactNode, cloneElement, useEffect } from 'react';
import classNames from 'classnames';
import {
  arrow as arrowMiddleware,
  offset,
  shift,
  autoUpdate,
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  useClick,
  useHover,
  hide as hideMiddleware,
  autoPlacement,
  FloatingPortal,
} from '@floating-ui/react';
import { Placement, placementToCSSProperty } from '../../styles';
import { CloseButton } from '../button';
import { OpenerTriggerEvent } from '../tooltip';
import { HelpTextProps } from '../text';
import { PopoverRoundedBase } from './PopoverRoundedBase';
import { getZIndex } from '../../styles';
import { FLOATING_ROOT_ID } from '../../constants';
import { Stack } from '../stack';
import { semantic_colors } from '../../foundation';
import { BaseText } from '../text';

type PopoverContent = ReactElement<HelpTextProps> | ReactNode;
type PopoverWithCloseAction = (action: { close(): void }) => PopoverContent;

export interface PopoverProps {
  className?: string;
  children: ReactNode;
  /**
   * Popover 내부에 렌더될 자유 콘텐츠 요소
   *  - HelpText 컴포넌트 규격 또는 자유롭게 콘텐츠를 구성할 경우 사용합니다.
   *  - PopoverContent
   *   - [HelpText](https://storybook.kakaostyle.in/master/pds/?path=/story/component-text-helptext--base) 또는 ReactNode 타입을 가집니다.
   *  - PopoverWithCloseAction
   *   - PopoverContent function 타입으로 가집니다.
   */
  content?: PopoverContent | PopoverWithCloseAction;
  title?: string;
  /**
   * 기본 폰트 Body_13_Regular적용
   */
  description?: ReactNode;
  label?: ReactNode;
  /**
   * 닫힘버튼 사용 유무
   * @default false
   */
  closable?: boolean;
  /**
   * main app node 바깥영역으로 생성할지 여부
   * @default false
   */
  noUsePortal?: boolean;
  /** @default getZIndex('popover') */
  zIndex?: number;
  /** 미지정인경우 자동 배치 처리됩니다. */
  placement?: Placement;
  /** @default 'hover' */
  openerTriggerEvent?: OpenerTriggerEvent;
  /**
   * outside 영역 클릭시 닫힘 사용 여부
   * @default true
   */
  shouldCloseOnOutsideClick?: boolean;
  /**
   * 화살표 사용 여부
   *  - false로 지정할 경우, 화살표가 빠지고 border-radius와 box-shadow가 적용됩니다.
   *   - placement의 기본값은 'bottom-start' 로 변경 됩니다. (자동 배치 되지 않음)
   *   - openerTriggerEvent의 기본값은 'click' 로 변경 됩니다.
   * @default true
   */
  arrow?: boolean;
  /**
   * 팝오버 내부여백
   *
   * 미지정 시 기본값:
   * - arrow=true: title/description 영역 16px 20px, content 영역 0
   * - arrow=false: 모든 영역 16px
   */
  padding?: number;
  /**
   * 팝오버 너비
   * @default 300
   */
  width?: number;
  open?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
}

export const Popover: React.FC<PopoverProps> = ({
  className,
  noUsePortal = false,
  zIndex = getZIndex('popover'),
  placement,
  openerTriggerEvent,
  shouldCloseOnOutsideClick = true,
  content,
  children,
  title,
  description,
  label,
  closable = false,
  arrow = true,
  padding,
  width = 300,
  open: openProp,
  onClose,
  onOpen,
}) => {
  if (!content && !title && !description)
    throw new Error('At least one content element must exist.(content, title, description)');

  const [opened, setOpened] = useState(openProp ?? false);

  const handleClose = () => {
    onClose ? onClose() : setOpened(false);
  };

  const arrowElement = useRef<HTMLDivElement | null>(null);
  const {
    strategy,
    x,
    y,
    refs,
    context,
    placement: currentPlacement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {}, hide },
  } = useFloating({
    open: opened,
    placement: arrow || placement ? placement : 'bottom-start',
    onOpenChange: setOpened,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(arrow ? 16 : 4),
      shift(),
      ...(arrow && !placement ? [autoPlacement()] : []),
      arrow ? arrowMiddleware({ element: arrowElement }) : null,
      hideMiddleware(),
    ],
  });

  const isEmptyOpenerTriggerEvent = !openerTriggerEvent || openerTriggerEvent === 'none';

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: openerTriggerEvent === 'hover' || (arrow && isEmptyOpenerTriggerEvent),
      delay: {
        open: 0,
        close: 250,
      },
    }),
    useClick(context, { enabled: openerTriggerEvent === 'click' || (!arrow && isEmptyOpenerTriggerEvent) }),
    useRole(context),
    useDismiss(context, { outsidePress: shouldCloseOnOutsideClick }),
  ]);

  useEffect(() => {
    if (openProp !== undefined) {
      setOpened(openProp);
    }
  }, [openProp]);

  useEffect(() => {
    opened ? onOpen?.() : onClose?.();
  }, [opened]);

  const renderSection = () =>
    (closable || title || description) && (
      <Stack direction='column' width={width} p={padding ?? (arrow ? '16px 20px' : 16)} gap={4}>
        {(title || closable) && (
          <Stack direction='row' gap={12} align='center'>
            {title && (
              <BaseText typography='Body_15_SemiBold' color={semantic_colors.content.primary}>
                {title}
              </BaseText>
            )}
            {closable && (
              <Stack direction='row' ml='auto'>
                <CloseButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  size={20}
                  color={semantic_colors.content.tertiary}
                />
              </Stack>
            )}
          </Stack>
        )}
        {description}
        {label}
      </Stack>
    );

  const renderContent = () => (typeof content === 'function' ? content({ close: handleClose }) : content);

  const renderToPopoverBase = () => {
    const content = renderContent();
    return (
      <PopoverRoundedBase
        arrow={arrow}
        {...getFloatingProps({
          ref: refs.setFloating,
          className: classNames('pds-popover', className),
          style: {
            position: strategy,
            top: y ?? '',
            left: x ?? '',
            zIndex,
            display: hide?.referenceHidden ? 'none' : 'block',
          },
        })}
      >
        {renderSection()}
        {content && (
          <Stack direction='column' p={padding ?? (arrow ? 0 : 16)}>
            {content}
          </Stack>
        )}
        {arrow && (
          <div
            className={classNames('pds-popover__arrow', currentPlacement)}
            ref={arrowElement}
            css={{
              left: arrowX != null ? `${arrowX}px` : '',
              top: arrowY != null ? `${arrowY}px` : '',
              [placementToCSSProperty(currentPlacement as Placement)]: '-7px',
            }}
          >
            <svg xmlns='http://www.w3.org/2000/svg' width='14' height='10' viewBox='0 0 14 10' fill='none'>
              <path
                d='M8.63847 8.15933L14 0.5H0L5.36154 8.15934C6.15774 9.29677 7.84226 9.29677 8.63847 8.15933Z'
                fill='white'
              />
            </svg>
          </div>
        )}
      </PopoverRoundedBase>
    );
  };

  return (
    <>
      {cloneElement(
        children as React.ReactElement,
        getReferenceProps({
          ref: refs.setReference,
          ...(children as React.ReactElement).props,
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            const childrenProps = (children as React.ReactElement).props;
            childrenProps.onClick && (childrenProps as Record<'onClick', React.MouseEventHandler>).onClick(e);
          },
        }),
      )}
      {opened &&
        (noUsePortal ? (
          renderToPopoverBase()
        ) : (
          <FloatingPortal id={FLOATING_ROOT_ID}>{renderToPopoverBase()}</FloatingPortal>
        ))}
    </>
  );
};
